/**
 * @typedef {{ x: number, y: number, width: number, height: number }} ClientRect
 * @typedef {{ axis: 'H' | 'V', distance: number, x1: number, y1: number, x2: number, y2: number }} SpacingSegment
 */

function intervalOverlap(a0, a1, b0, b1) {
  const lo = Math.max(a0, b0);
  const hi = Math.min(a1, b1);
  return hi > lo ? hi - lo : 0;
}


export function unionClientRects(nodes, relativeToStage) {
  if (!nodes?.length) return null;
  const rectOpts = relativeToStage ? { relativeTo: relativeToStage } : {};
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    if (!n?.getClientRect) continue;
    const r = n.getClientRect(rectOpts);
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  if (minX === Infinity) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * @param {ClientRect} a
 * @param {ClientRect} b
 * @returns {SpacingSegment[]}
 */
export function getSpacingSegmentsBetweenRects(a, b) {
  const ax2 = a.x + a.width;
  const ay2 = a.y + a.height;
  const bx2 = b.x + b.width;
  const by2 = b.y + b.height;
  /** @type {SpacingSegment[]} */
  const out = [];

  const yOverlap = intervalOverlap(a.y, ay2, b.y, by2);
  if (yOverlap > 0) {
    if (ax2 <= b.x) {
      const gap = b.x - ax2;
      const yMid = (Math.max(a.y, b.y) + Math.min(ay2, by2)) / 2;
      out.push({ axis: 'H', distance: gap, x1: ax2, y1: yMid, x2: b.x, y2: yMid });
    } else if (bx2 <= a.x) {
      const gap = a.x - bx2;
      const yMid = (Math.max(a.y, b.y) + Math.min(ay2, by2)) / 2;
      out.push({ axis: 'H', distance: gap, x1: bx2, y1: yMid, x2: a.x, y2: yMid });
    }
  }

  const xOverlap = intervalOverlap(a.x, ax2, b.x, bx2);
  if (xOverlap > 0) {
    if (ay2 <= b.y) {
      const gap = b.y - ay2;
      const xMid = (Math.max(a.x, b.x) + Math.min(ax2, bx2)) / 2;
      out.push({ axis: 'V', distance: gap, x1: xMid, y1: ay2, x2: xMid, y2: b.y });
    } else if (by2 <= a.y) {
      const gap = a.y - by2;
      const xMid = (Math.max(a.x, b.x) + Math.min(ax2, bx2)) / 2;
      out.push({ axis: 'V', distance: gap, x1: xMid, y1: by2, x2: xMid, y2: a.y });
    }
  }

  return out;
}

/**
 * @param {ClientRect} inner 
 * @param {ClientRect} outer
 * @returns {SpacingSegment[]}
 */
export function getInsetSegmentsInnerToOuter(inner, outer) {
  const ox2 = outer.x + outer.width;
  const oy2 = outer.y + outer.height;
  const ix2 = inner.x + inner.width;
  const iy2 = inner.y + inner.height;
  const cx = inner.x + inner.width / 2;
  const cy = inner.y + inner.height / 2;
  /** @type {SpacingSegment[]} */
  const out = [];

  const leftGap = inner.x - outer.x;
  if (leftGap > 0.25) {
    out.push({
      axis: 'H',
      distance: leftGap,
      x1: outer.x,
      y1: cy,
      x2: inner.x,
      y2: cy,
    });
  }

  const rightGap = ox2 - ix2;
  if (rightGap > 0.25) {
    out.push({
      axis: 'H',
      distance: rightGap,
      x1: ix2,
      y1: cy,
      x2: ox2,
      y2: cy,
    });
  }

  const topGap = inner.y - outer.y;
  if (topGap > 0.25) {
    out.push({
      axis: 'V',
      distance: topGap,
      x1: cx,
      y1: outer.y,
      x2: cx,
      y2: inner.y,
    });
  }

  const bottomGap = oy2 - iy2;
  if (bottomGap > 0.25) {
    out.push({
      axis: 'V',
      distance: bottomGap,
      x1: cx,
      y1: iy2,
      x2: cx,
      y2: oy2,
    });
  }

  return out;
}

/**
 * @param {import('konva').Node} skipShape
 * @param {import('konva').Group} guideItem
 * @param {typeof import('konva')} Konva
 */
export function shouldIncludeElementGroupForSpacing(skipShape, guideItem, Konva) {
  if (skipShape instanceof Konva.Transformer) {
    if (skipShape.nodes().indexOf(guideItem) >= 0) return false;
  } else if (
    guideItem === skipShape ||
    skipShape.find('.elementGroup').indexOf(guideItem) >= 0
  ) {
    return false;
  }
  return true;
}

/**
 * @param {import('konva').Node} skipShape
 * @param {import('konva').Group} movingGroup
 * @param {typeof import('konva')} Konva
 * @param {import('konva').Node[]} elementGroups
 * @param {import('konva').Stage} relativeToStage
 * @returns {SpacingSegment[]}
 */
export function collectSpacingSegmentsToNeighbors(skipShape, movingGroup, Konva, elementGroups, relativeToStage) {
  if (!skipShape || !movingGroup?.getClientRect || !relativeToStage) return [];
  const rectOpts = { relativeTo: relativeToStage };
  const a = movingGroup.getClientRect(rectOpts);
  /** @type {SpacingSegment[]} */
  const all = [];
  for (const other of elementGroups) {
    if (!shouldIncludeElementGroupForSpacing(skipShape, other, Konva)) continue;
    all.push(...getSpacingSegmentsBetweenRects(a, other.getClientRect(rectOpts)));
  }
  return all;
}
